/**
 * The merchant picks categories, not extensions (DIV-012). Categories keep the
 * builder to five decisions instead of twenty-odd, and keep pairs like
 * .jpg/.jpeg from being half-picked — a shopper hitting "JPEG not allowed"
 * because the merchant ticked only one spelling is the failure this avoids.
 *
 * An EMPTY or missing list means no restriction. That is what every element
 * saved before this feature has, and it must keep meaning "anything goes".
 */
const CATEGORY_EXTENSIONS: Record<string, string[]> = {
  images: [".jpg", ".jpeg", ".png", ".gif", ".webp"],
  pdf: [".pdf"],
  documents: [".doc", ".docx", ".txt", ".rtf"],
  spreadsheets: [".xls", ".xlsx", ".csv"],
  design: [".ai", ".eps", ".psd", ".svg"],
};

/** Declaration order, so the accept attribute is stable whatever order the merchant ticked in. */
const CATEGORY_ORDER = Object.keys(CATEGORY_EXTENSIONS);

/** The extensions an element allows, or [] when it allows everything. */
export function allowedExtensions(allowedFileTypes: unknown): string[] {
  if (!Array.isArray(allowedFileTypes) || allowedFileTypes.length === 0) return [];
  const out: string[] = [];
  for (const category of CATEGORY_ORDER) {
    if (allowedFileTypes.includes(category)) out.push(...CATEGORY_EXTENSIONS[category]!);
  }
  return out;
}

/** ".jpg" → "JPG", for the line under the drop zone and the refusal message. */
export function extensionLabels(extensions: string[]): string {
  return extensions.map((ext) => ext.replace(".", "").toUpperCase()).join(", ");
}

/** The extension of a filename, lower-cased and dotted; "" when it has none. */
export function fileExtension(fileName: string): string {
  const dot = fileName.lastIndexOf(".");
  return dot > 0 ? fileName.slice(dot).toLowerCase() : "";
}

/** Whether this file may be uploaded. An empty allow-list permits everything. */
export function isAllowedFile(fileName: string, extensions: string[]): boolean {
  if (extensions.length === 0) return true;
  return extensions.includes(fileExtension(fileName));
}

/** Extensions we are willing to render as a thumbnail preview. */
const THUMBNAILABLE = [".jpg", ".jpeg", ".png", ".gif", ".webp"];

/**
 * "1.8 MB". Anything under a kilobyte rounds up, because "0 KB" next to a file the
 * shopper just watched upload reads as a failure.
 */
export function formatFileSize(bytes: number): string {
  if (!isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return "1 KB";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

/**
 * Note .svg is an allowed design file but is deliberately NOT thumbnailed: it is
 * untrusted markup from a shopper, and putting it in an <img> preview is a
 * decision to make on its own rather than by omission.
 */
export function isImageFile(fileName: string): boolean {
  const lower = fileName.toLowerCase();
  return THUMBNAILABLE.some((ext) => lower.endsWith(ext));
}
